import { useEffect, useRef, useState } from 'react';
import { Heading, Text, Muted, Button, ThemeToggle, AudioButton } from '@fluent/ui';
import './VoicePilotReview.css';

/**
 * /voice-pilot-dialogue — Phase 12 follow-up.
 *
 * The first voice pilot picked alloy as the single narrator voice for
 * the entire app (DECISIONS.md 2026-04-25). On Tornike's pilot phone
 * playthrough (2026-04-29) he flagged that mini-dialogues with two
 * speakers in one alloy voice sound like "a quickly read text", not a
 * real conversation. So we're adding TWO character voices — one male
 * for Giorgi, one female for Mariam — which run only inside dialogue
 * exercises. Solo phrases keep alloy.
 *
 * Five candidates, one short line each. Pick one male + one female.
 */

const MALE_CANDIDATES = ['onyx', 'echo', 'fable'] as const;
const FEMALE_CANDIDATES = ['nova', 'shimmer'] as const;

type MaleVoice = (typeof MALE_CANDIDATES)[number];
type FemaleVoice = (typeof FEMALE_CANDIDATES)[number];

const MALE_NOTES: Record<MaleVoice, string> = {
  onyx: 'Deep, calm, mature. Confident — feels older than 19.',
  echo: 'Warm, mid-range. Friendly without being childish.',
  fable: 'Warm, slightly British accent. Story-teller cadence.',
};
const FEMALE_NOTES: Record<FemaleVoice, string> = {
  nova: 'Clean, bright, energetic. Closest to the alloy default vibe.',
  shimmer: 'Softer, slightly airy. Gentler than nova.',
};

const MALE_LINE = 'Hello, I am Giorgi. How are you?';
const FEMALE_LINE = 'My name is Mariam. I am fine, thanks.';

export function VoicePilotDialogueReview() {
  const [malePick, setMalePick] = useState<MaleVoice | null>(null);
  const [femalePick, setFemalePick] = useState<FemaleVoice | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingKey, setPlayingKey] = useState<string | null>(null);

  useEffect(() => {
    const a = new Audio();
    a.preload = 'auto';
    a.addEventListener('ended', () => setPlayingKey(null));
    audioRef.current = a;
    return () => {
      a.pause();
      audioRef.current = null;
    };
  }, []);

  const play = (voice: string) => {
    const a = audioRef.current;
    if (!a) return;
    if (playingKey === voice) {
      a.pause();
      a.currentTime = 0;
      setPlayingKey(null);
      return;
    }
    a.src = `/voice-pilot-dialogue/${voice}.mp3`;
    a.currentTime = 0;
    setPlayingKey(voice);
    void a.play().catch(() => setPlayingKey(null));
  };

  return (
    <main className="vpilot">
      <header className="vpilot__hero">
        <p className="vpilot__kicker">Phase 12 · Dialogue voices</p>
        <Heading level={2} tone="inverse">
          აარჩიე ხმები გიორგისთვის და მარიამისთვის
        </Heading>
        <Text tone="inverse" size="base" className="vpilot__lede">
          Pick one male voice for Giorgi + one female voice for Mariam. These run only inside dialogue
          exercises. Every other line in the app stays in alloy (the locked narrator voice).
        </Text>
        <div className="vpilot__theme">
          <ThemeToggle />
        </div>
      </header>

      <section className="vpilot__grid">
        {MALE_CANDIDATES.map((voice) => (
          <article key={voice} className="vpilot__card" data-picked={malePick === voice || undefined}>
            <header className="vpilot__cardhead">
              <Heading level={3}>{voice}</Heading>
              <Muted>Giorgi · {MALE_NOTES[voice]}</Muted>
            </header>
            <ul className="vpilot__phrases">
              <li className="vpilot__phrase">
                <AudioButton
                  size="sm"
                  playing={playingKey === voice}
                  onClick={() => play(voice)}
                  label={`Play ${voice} — ${MALE_LINE}`}
                />
                <div className="vpilot__lines">
                  <Text size="base" weight="bold">
                    {MALE_LINE}
                  </Text>
                </div>
              </li>
            </ul>
            <footer className="vpilot__cardfoot">
              <Button
                variant={malePick === voice ? 'primary' : 'neutral'}
                size="md"
                fullWidth
                onClick={() => setMalePick(voice)}
              >
                {malePick === voice ? '✓ Picked for Giorgi' : `Pick ${voice} for Giorgi`}
              </Button>
            </footer>
          </article>
        ))}
        {FEMALE_CANDIDATES.map((voice) => (
          <article key={voice} className="vpilot__card" data-picked={femalePick === voice || undefined}>
            <header className="vpilot__cardhead">
              <Heading level={3}>{voice}</Heading>
              <Muted>Mariam · {FEMALE_NOTES[voice]}</Muted>
            </header>
            <ul className="vpilot__phrases">
              <li className="vpilot__phrase">
                <AudioButton
                  size="sm"
                  playing={playingKey === voice}
                  onClick={() => play(voice)}
                  label={`Play ${voice} — ${FEMALE_LINE}`}
                />
                <div className="vpilot__lines">
                  <Text size="base" weight="bold">
                    {FEMALE_LINE}
                  </Text>
                </div>
              </li>
            </ul>
            <footer className="vpilot__cardfoot">
              <Button
                variant={femalePick === voice ? 'primary' : 'neutral'}
                size="md"
                fullWidth
                onClick={() => setFemalePick(voice)}
              >
                {femalePick === voice ? '✓ Picked for Mariam' : `Pick ${voice} for Mariam`}
              </Button>
            </footer>
          </article>
        ))}
      </section>

      <section className="vpilot__lock" aria-live="polite">
        {malePick && femalePick ? (
          <Text size="base">
            Tornike picked <strong>{malePick}</strong> for Giorgi + <strong>{femalePick}</strong> for
            Mariam. Tell Jake to lock these in DECISIONS.md and rebuild the dialogue.
          </Text>
        ) : (
          <Muted>Pick one male voice and one female voice. Compare each by tapping ▶.</Muted>
        )}
      </section>
    </main>
  );
}
